<template>
	<CAppNav
		:global-store="globalStore"
		:search-visible="searchVisible"
		@close-search="handleSearchClose"
		@select-entry-view="selectEntryView"
		@show-account-menu="$emit('show-account-menu')"
	/>
</template>

<script lang="ts">
	import { defineComponent, inject, ref, computed } from 'vue';
	import type { PropType } from 'vue';
	import CAppNav from './AppNav.vue';
	import type { GlobalStore } from 'src/stores/global-store';
	import { useRoute, useRouter } from 'vue-router/composables';
	import { isDeviceType } from '@cognitoforms/utils/user-agent';
	import { navStateKey } from 'src/utilities/injection-keys';
	import type { SelectEvent } from './FormTreeContainer.vue';
	import { NavigationFailureType, isNavigationFailure } from 'vue-router';
	import { useHotKey } from 'src/composables/use-hot-key';

	export default defineComponent({
		name: 'CAppNavContainer',
		components: {
			CAppNav
		},
		props: {
			globalStore: {
				type: Object as PropType<GlobalStore>,
				required: true
			}
		},
		emits: ['show-account-menu'],
		setup(props) {
			const route = useRoute();
			const router = useRouter();
			const navState = inject(navStateKey);
			const isPopperOnlyState = computed(() => route.meta.showNavAsPopperOnly);
			const searchVisible = ref(null);

			const navigatingToForm = ref(null as string);
			const selectEntryView = (event: SelectEvent) => {
				const [ formId, viewNumber ] = event.id.split('-');
				const form = props.globalStore.forms[formId];
				const view = form.EntryViews.find(v => v.Id === event.id);

				navigatingToForm.value = formId;

				router.push({
					name: 'entries',
					params: {
						orgcode: props.globalStore.organization.Code,
						formUrlName: form.UrlName,
						entryView: `${viewNumber}-${view.InternalName}`
					}
				}).catch(failure => {
					if (isNavigationFailure(failure, NavigationFailureType.aborted) && navigatingToForm.value !== formId) {
						// Ignore aborted navigation to entries page when concurrently selecting different forms or views in different forms
					}
					else if (isNavigationFailure(failure, NavigationFailureType.duplicated)) {
						// Ignore duplicate navigation
						// TODO: Engineering Bug #45747 : Clicking single-view form in app nav results in duplicate navigation errors in console
					}
					else if (isNavigationFailure(failure, NavigationFailureType.cancelled)) {
						// Ignore cancelled navigation, happens when clicking different forms too fast
					}
					else if (isNavigationFailure(failure, NavigationFailureType.redirected)) {
						// Ignore redirected navigation when opening forms views in a lightbox
					}
					else {
						throw failure;
					}
				});
			};

			const handleSearchClose = () => {
				searchVisible.value = false;
			};

			const activeElIsEditable = () => {
				const activeEl = document.activeElement as HTMLElement;
				return activeEl.isContentEditable ||
					['INPUT', 'TEXTAREA'].includes(activeEl.nodeName) && !['checkbox', 'radio'].includes(activeEl.getAttribute('type')) ||
					activeEl.getAttribute('contenteditable') === 'true';
			};

			const toggleSidebarDocking = e => {
				const newState = isPopperOnlyState.value ? !(navState.isPopper.value && navState.showOverlay.value) : !navState.isOpen.value;

				if (isPopperOnlyState.value)
					navState.setIsPopper(newState, newState);
				else
					navState.setIsOpen(newState);

				if (newState) {
					document.body.tabIndex = -1;
					document.body.focus();
					e?.preventDefault();
				}
			};

			useHotKey('Escape', () => navState.isPopper.value, () => {
				navState.setIsPopper(false, false);
				navState.setIsHandlingInteraction(false);
			});

			useHotKey('/', () => !activeElIsEditable(), () => {
				if (!navState.isOpen.value)
					navState.setIsPopper(true);

				if (searchVisible.value && document.getElementById('search'))
					document.getElementById('search').focus();

				searchVisible.value = true;
			});

			useHotKey('[', () => !activeElIsEditable(), e => toggleSidebarDocking(e));

			useHotKey('\\', e => (e.ctrlKey || (e.metaKey && isDeviceType(window.navigator, 'mac-desktop'))), e => {
				toggleSidebarDocking(e);
			});

			return {
				selectEntryView,
				searchVisible,
				handleSearchClose
			};
		}
	});
</script>